import { Link as RouterLink } from "react-router";
import { Button, Text, makeStyles, tokens } from "@fluentui/react-components";
import { CalendarArrowRight24Regular, Chat24Regular, PersonAvailable24Regular, Sparkle24Regular } from "@fluentui/react-icons";
import { Wordmark } from "../components/Logo.tsx";

const useStyles = makeStyles({
  page: { minHeight: "100vh", backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorNeutralForeground1 },
  wrap: { width: "min(100%, 1080px)", marginLeft: "auto", marginRight: "auto", paddingLeft: "24px", paddingRight: "24px" },
  nav: { display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "20px", paddingBottom: "20px" },
  navLinks: { display: "flex", gap: "8px", alignItems: "center" },
  hero: {
    display: "grid",
    gridTemplateColumns: "1.1fr 1fr",
    gap: "40px",
    alignItems: "center",
    paddingTop: "40px",
    paddingBottom: "56px",
    "@media (max-width: 800px)": { gridTemplateColumns: "1fr", paddingTop: "16px" },
  },
  h1: { fontSize: "44px", lineHeight: "1.1", fontWeight: tokens.fontWeightBold, letterSpacing: "-0.02em", margin: 0, "@media (max-width: 800px)": { fontSize: "34px" } },
  lead: { fontSize: tokens.fontSizeBase500, lineHeight: "1.5", color: tokens.colorNeutralForeground2, marginTop: "16px", marginBottom: "24px", maxWidth: "48ch" },
  ctas: { display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" },
  photo: { width: "100%", height: "auto", borderRadius: tokens.borderRadiusXLarge, boxShadow: tokens.shadow16, display: "block" },
  band: { backgroundColor: tokens.colorNeutralBackground3, paddingTop: "56px", paddingBottom: "56px" },
  steps: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px", "@media (max-width: 800px)": { gridTemplateColumns: "1fr 1fr" }, "@media (max-width: 480px)": { gridTemplateColumns: "1fr" } },
  step: { backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge, padding: "20px", boxShadow: tokens.shadow2, display: "flex", flexDirection: "column", gap: "8px" },
  stepIcon: { color: tokens.colorBrandForeground1, display: "flex" },
  split: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px", alignItems: "center", paddingTop: "56px", paddingBottom: "56px", "@media (max-width: 800px)": { gridTemplateColumns: "1fr" } },
  list: { margin: 0, paddingLeft: "20px", lineHeight: "1.7", color: tokens.colorNeutralForeground2 },
  footer: { borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: "24px", paddingBottom: "32px", display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "space-between", color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  footLinks: { display: "flex", gap: "16px" },
  h2: { fontSize: "28px", lineHeight: "1.2", fontWeight: tokens.fontWeightSemibold, margin: 0, marginBottom: "8px", letterSpacing: "-0.01em" },
  sub: { color: tokens.colorNeutralForeground3, marginBottom: "28px", display: "block" },
});

const STEPS = [
  { icon: <PersonAvailable24Regular />, title: "Set your week", body: "Block out when you can train and how long a session is. Change it any week." },
  { icon: <Chat24Regular />, title: "Clients reply on WhatsApp", body: "Every week RackUp asks each client when they can make it. They just text back." },
  { icon: <Sparkle24Regular />, title: "RackUp understands the replies", body: "“Can't do Monday, 8 instead of 6 on Wednesday” becomes a plan, not a to-do." },
  { icon: <CalendarArrowRight24Regular />, title: "Schedule goes out", body: "Everyone fits? It publishes itself. Someone doesn't? You get told, not surprised." },
];

export function LandingPage() {
  const s = useStyles();
  return (
    <div className={s.page}>
      <header className={s.wrap}>
        <nav className={s.nav}>
          <Wordmark height={38} />
          <div className={s.navLinks}>
            <Button as="a" href="/login" appearance="subtle">
              Sign in
            </Button>
            <Button as="a" href="/register" appearance="primary">
              Get started
            </Button>
          </div>
        </nav>
      </header>

      <main>
        <section className={s.wrap}>
          <div className={s.hero}>
            <div>
              <h1 className={s.h1}>Your clients&rsquo; sessions, booked by WhatsApp. Every week, without the back-and-forth.</h1>
              <p className={s.lead}>
                RackUp is for personal trainers. It asks each client when they can train, understands the replies, and fills your week around your
                availability. You coach; it does the diary.
              </p>
              <div className={s.ctas}>
                <Button as="a" href="/register" appearance="primary" size="large">
                  Create your trainer account
                </Button>
                <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
                  Free while in trial · takes about a minute
                </Text>
              </div>
            </div>
            <img className={s.photo} src="/hero-coaching.jpg" alt="A personal trainer coaching a client through a kettlebell lift" width={1600} height={900} />
          </div>
        </section>

        <section className={s.band}>
          <div className={s.wrap}>
            <h2 className={s.h2}>How a week works</h2>
            <Text className={s.sub}>Set it up once. After that it runs on the day you choose.</Text>
            <div className={s.steps}>
              {STEPS.map((st, i) => (
                <div className={s.step} key={st.title}>
                  <span className={s.stepIcon}>{st.icon}</span>
                  <Text weight="semibold" size={400}>
                    {i + 1}. {st.title}
                  </Text>
                  <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                    {st.body}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={s.wrap}>
          <div className={s.split}>
            <img className={s.photo} src="/hero-planning.jpg" alt="A trainer and client reviewing the week's plan on a tablet" width={1600} height={900} />
            <div>
              <h2 className={s.h2}>Built for the way PTs actually work</h2>
              <ul className={s.list}>
                <li>One gym, your hours, your session length. Holiday weeks in one tap.</li>
                <li>Clients never install anything — WhatsApp plus a private link to update their preferences.</li>
                <li>Regulars who don&rsquo;t reply keep their usual slots. Hard-to-place clients get priority.</li>
                <li>Review before it goes out, or let it auto-publish when everything fits.</li>
                <li>You&rsquo;re told when a client opts out or leaves. Nothing silently disappears.</li>
                <li>UK &amp; EU GDPR built in: consent on join, data export and deletion for everyone.</li>
              </ul>
              <div style={{ marginTop: 24 }}>
                <Button as="a" href="/register" appearance="primary">
                  Get started
                </Button>
              </div>
            </div>
          </div>
        </section>
        <section className={s.band}>
          <div className={s.wrap}>
            <div className={s.split} style={{ paddingTop: 0, paddingBottom: 0 }}>
              <div>
                <h2 className={s.h2}>Your clients get a simpler week too</h2>
                <ul className={s.list}>
                  <li>One WhatsApp a week: &ldquo;Same as usual?&rdquo; They reply in their own words.</li>
                  <li>Their sessions arrive as a message. No app, no account, no password.</li>
                  <li>A private link if they want to change their usual times.</li>
                  <li>Reply STOP any time; their data is theirs to download or delete.</li>
                </ul>
                <div style={{ marginTop: 24, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <Button as="a" href="/register" appearance="primary" size="large">
                    Create your trainer account
                  </Button>
                  <Button as="a" href="/login" appearance="subtle">
                    Already a trainer? Sign in
                  </Button>
                </div>
              </div>
              <img className={s.photo} src="/hero-lunge.jpg" alt="A trainer coaching a client through a dumbbell lunge" width={1600} height={900} />
            </div>
          </div>
        </section>
      </main>

      <footer className={s.wrap}>
        <div className={s.footer}>
          <span>© {new Date().getFullYear()} Copper Beech AI Ltd · Company no. NI739924 · Northern Ireland</span>
          <span className={s.footLinks}>
            <RouterLink to="/privacy">Privacy</RouterLink>
            <RouterLink to="/privacy/trainers">Trainer notice</RouterLink>
            <RouterLink to="/privacy/terms">Terms</RouterLink>
            <RouterLink to="/login">Sign in</RouterLink>
          </span>
        </div>
      </footer>
    </div>
  );
}
